import { call, delay, put, takeLatest, select } from 'redux-saga/effects';
import { callWithAuth } from '../sagaApiHelper';
import { handleFileUpload } from './fileUploadSaga';
import { showAlert } from '../alertSlice';
import { addComplaintsApi, getComplaintDataApi } from '../../api/apiLists/addComplaintApi';
import { clearFiles } from '../filesSlice';
import {
    setComplaintId,
    setNoteSuccess,
    setNoteLoading,
    setComplaintStatus,
    setCompleteComplaintData,
    setComplaintDataError,
    setComplaintDataLoading,
} from '../complaintSlice';
import { hideLoader, showLoader } from '../loaderSlice';
import { loadListDataRequest, resetListData } from '../listSlice';
import { listPath } from '../../utils/constants/insightsPath';

function* handleAddComplaint(action) {

    try {
        yield put(showLoader());
        // // console.log("action", action.payload);
        const { imagePath, ...otherPayload } = action.payload;
        const uploadedFiles = yield call(handleFileUpload, { path: imagePath, customerId: otherPayload.customerId });
        // // console.log("uploadedFiles", uploadedFiles);
        const images = uploadedFiles.map(file => ({
            name: file.name,
            url: file.fileURL
        }));
        const finalPayload = {
            ...otherPayload,
            images,
            apiVerb: "ADD"
        };
        const res = yield call(callWithAuth, addComplaintsApi, finalPayload);

        if (res?.status === true) {
            yield put(clearFiles({ key: imagePath }));
            yield delay(3000);
            yield put(setComplaintId(res?.data));
            yield put(showAlert({ message: "Complaint Raised Successfully", type: 'success' }));
        } else {
            yield put(showAlert({ message: res.msg ? res.msg : 'Error Occured Please Try Again.', type: 'error' }));
        }
        yield put(hideLoader());
    } catch (error) {
        console.error("Error fetching list data:", error);
        yield put(showAlert({ message: "Error Occured Please Try Again.", type: 'error' }));
        yield put(hideLoader());
    }
}

function* handleEditComplaint(action) {
    // // console.log(action.payload)
    try {
        yield put(showLoader());
        // console.log("Payload : ", action.payload);
        const {
            imagePath,
            id,
            userId,
            removedImageUrls,
            ...otherPayload
        } = action.payload;

        let addedImages = [];

        if (imagePath) {
            const uploadedFiles = yield call(handleFileUpload, {
                path: imagePath,
                customerId: otherPayload.customerId
            });
            // // console.log("uploadedFiles for edit", uploadedFiles);
            addedImages = uploadedFiles?.map(file => ({
                name: file.name,
                url: file.fileURL
            }));
        }

        const finalPayload = {
            ...otherPayload,
            id,
            userId,
            apiVerb: "UPDATE",
            ...(addedImages?.length > 0 && { addedImages }),
            ...(removedImageUrls?.length > 0 && { removedImageUrls }),
        };

        // // console.log("Final payload for edit complaint:", finalPayload);
        const res = yield call(callWithAuth, addComplaintsApi, finalPayload);

        if (res?.status === true) {
            if (imagePath) {
                yield put(clearFiles({ key: imagePath }));
            }
            yield put(setComplaintId(id));
            yield put(showAlert({ message: "Complaint Updated Successfully", type: 'success' }));
            yield put(resetListData(listPath.COMPLAINTS))
            yield delay(2000);
            const isOnComplaintRoute = yield select(state => state.complaint.isOnComplaintRoute);
            yield put(
                loadListDataRequest({
                    key: listPath.COMPLAINTS,
                    filterConditions: isOnComplaintRoute ? {} : { userId: userId },
                    offset: 0,
                })
            );
        } else {
            yield put(showAlert({ message: res.msg ? res.msg : 'Error Occurred Please Try Again.', type: 'error' }));
        }
        yield put(hideLoader());
    } catch (error) {
        console.error("Error editing complaint:", error);
        yield put(showAlert({ message: "Error Occurred Please Try Again.", type: 'error' }));
        yield put(hideLoader());
    }
}

function* handleAddNote(action) {
    try {
        yield put(setNoteLoading(true));
        // // console.log("Add note action", action.payload);

        const finalPayload = {
            complaintId: action.payload.complaintId,
            customerId: action.payload.customerId,
            description: action.payload.description,
            apiVerb: "ADD_NOTE"
        };

        const addNoteApiCall = (payload, authHeaders) => {
            return addComplaintsApi(payload, authHeaders, "ADD_NOTE");
        };

        const res = yield call(callWithAuth, addNoteApiCall, finalPayload);


        if (res?.status === true) {
            yield put(setNoteSuccess(true));
            yield put(showAlert({ message: "Note Added Successfully", type: 'success' }));
        } else {
            yield put(showAlert({ message: res.msg ? res.msg : 'Error Occurred Please Try Again.', type: 'error' }));
        }
        yield put(setNoteLoading(false));
    } catch (error) {
        console.error("Error adding note:", error);
        yield put(showAlert({ message: "Error Occurred Please Try Again.", type: 'error' }));
        yield put(setNoteLoading(false));
    }
}

function* handleGetComplaintData(action) {
    try {
        // Set loading state
        yield put(setComplaintDataLoading(true));

        const finalPayload = {
            id: action.payload.complaintId,
        };

        const res = yield call(callWithAuth, getComplaintDataApi, finalPayload);
        // console.log(res);

        if (res?.status === true) {
            // Get categories from store to properly format complaint data
            const categories = yield select(state => state.categories);
            
            const complaintData = res.data;
            
            // Format complaint data with proper category and subcategory labels
            const getCategoryLabel = (categoryId) => {
                return categories.serviceRequestCategories.find((cat) => cat.value === categoryId)?.label || categoryId;
            };
            
            const getSubCategoryLabel = (categoryId, subCategoryId) => {
                const subcats = categories.serviceRequestSubcategories[categoryId] || [];
                return subcats.find((sub) => sub.value === subCategoryId)?.label || subCategoryId;
            };
            
            // Add formatted category and subcategory objects to the complaint data
            const formattedData = {
                ...complaintData,
                categoryObject: {
                    value: complaintData.complaintCategory,
                    label: getCategoryLabel(complaintData.complaintCategory),
                },
                complaintSubCategoryObject: {
                    value: complaintData.complaintSubCategory,
                    label: getSubCategoryLabel(complaintData.complaintCategory, complaintData.complaintSubCategory),
                },
            };
            
            // Store complete complaint data in the slice
            yield put(setCompleteComplaintData(formattedData));
        } else {
            yield put(setComplaintDataError(res.msg || 'Failed to fetch complaint data'));
        }
    } catch (error) {
        console.error("Error fetching complaint data:", error);
        yield put(setComplaintDataError("Error occurred while fetching complaint data"));
    }
}

function* handleUpdateComplaintStatus(action) {
    try {
        yield put(showLoader());
        // console.log("Update complaint status action", action.payload);
        const { userId, ...otherPayload } = action.payload;

        const finalPayload = {
            ...otherPayload,
            userId,
            apiVerb: "UPDATE",
        };

        const res = yield call(callWithAuth, addComplaintsApi, finalPayload);

        if (res?.status === true) {
            yield put(showAlert({ message: "Complaint Status Updated Successfully", type: 'success' }));
            yield put(resetListData(listPath.COMPLAINTS))
            yield delay(2000);
            const isOnComplaintRoute = yield select(state => state.complaint.isOnComplaintRoute);
            yield put(
                loadListDataRequest({
                    key: listPath.COMPLAINTS,
                    filterConditions: isOnComplaintRoute ? {} : { userId: userId },
                    offset: 0,
                })
            );
            yield put(setComplaintStatus(action.payload.status));
        } else {
            yield put(showAlert({ message: res.msg ? res.msg : 'Error Occurred Please Try Again.', type: 'error' }));
        }
        yield put(hideLoader());
    } catch (error) {
        console.error("Error updating complaint status:", error);
        yield put(showAlert({ message: "Error Occurred Please Try Again.", type: 'error' }));
        yield put(hideLoader());
    }
}

export default function* complaintSaga() {
    yield takeLatest("RAISE_NEW_COMPLAINT", handleAddComplaint);
    yield takeLatest("EDIT_COMPLAINT", handleEditComplaint);
    yield takeLatest("ADD_NOTE", handleAddNote);
    yield takeLatest("GET_COMPLAINT_IMAGES", handleGetComplaintData);
    yield takeLatest("UPDATE_COMPLAINT_STATUS", handleUpdateComplaintStatus);
}
